import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: '',
    color: '',
  });
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    checkResetToken();
  }, []);

  const checkResetToken = async () => {
    try {
      console.log('=== PASSWORD RESET PAGE LOADED ===');
      console.log('Current URL:', window.location?.href);
      console.log('URL Hash:', window.location?.hash);
      
      // Supabase password reset links include hash fragments like:
      // #access_token=...&type=recovery&...
      // We need to check for these hash parameters
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams?.get('access_token');
      const tokenType = hashParams?.get('type');
      const refreshToken = hashParams?.get('refresh_token');

      console.log('Token type:', tokenType);
      console.log('Has access token:', !!accessToken);
      console.log('Has refresh token:', !!refreshToken);

      // Check if this is a valid password reset link
      if (tokenType === 'recovery' && accessToken) {
        console.log('✓ Valid recovery token detected');
        
        // Set the session using the tokens from the URL
        if (refreshToken) {
          console.log('Setting session with access and refresh tokens...');
          const { data, error } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('✗ Error setting session:', error);
            setIsValidToken(false);
            setErrors({ 
              token: 'Invalid or expired reset link. Please request a new password reset.',
              details: error?.message 
            });
          } else {
            console.log('✓ Session established successfully');
            console.log('User:', data?.user?.email);
            setIsValidToken(true);
          }
        } else {
          // Fallback: just having the access token is enough
          console.log('✓ Access token present (no refresh token)');
          setIsValidToken(true);
        }
      } else {
        // No valid recovery token in URL
        console.log('✗ No valid recovery token found in URL');
        console.log('This usually means:');
        console.log('  1. User navigated directly to this page (not from email link)');
        console.log('  2. Email link is redirecting to wrong page (check Supabase config)');
        console.log('  3. Token has expired or been used already');
        
        // Check if user already has an active session (edge case)
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (session && !error) {
          console.log('✓ Existing session found, allowing password reset');
          setIsValidToken(true);
        } else {
          console.log('✗ No active session found');
          setIsValidToken(false);
          setErrors({ 
            token: 'Invalid or expired reset link. Please request a new password reset.',
            diagnostic: 'No recovery token found in URL. If you clicked a link from your email, '+ 'please check that the Supabase email template is configured correctly to '+ 'redirect to /password-reset-page (not /user-login).'
          });
        }
      }
    } catch (err) {
      console.error('✗ Token verification error:', err);
      setIsValidToken(false);
      setErrors({ 
        token: 'Failed to verify reset link. Please try again.',
        details: err?.message 
      });
    } finally {
      setIsCheckingToken(false);
    }
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (!password) return { score: 0, label: '', color: '' };

    // Length check
    if (password?.length >= 8) score++;
    if (password?.length >= 12) score++;

    // Character variety checks
    if (/[a-z]/?.test(password)) score++;
    if (/[A-Z]/?.test(password)) score++;
    if (/[0-9]/?.test(password)) score++;
    if (/[^a-zA-Z0-9]/?.test(password)) score++;

    // Determine strength label and color
    if (score <= 2) return { score, label: 'Weak', color: 'bg-error' };
    if (score <= 4) return { score, label: 'Fair', color: 'bg-warning' };
    if (score <= 5) return { score, label: 'Good', color: 'bg-info' };
    return { score, label: 'Strong', color: 'bg-success' };
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }

    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/?.test(formData?.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/?.test(formData?.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/?.test(formData?.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      console.log('Updating password...');
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: formData?.password,
      });

      if (updateError) {
        console.error('✗ Password update failed:', updateError);
        setErrors({ submit: updateError?.message || 'Failed to reset password. Please try again.' });
        return;
      }

      console.log('✓ Password updated successfully');
      setSuccessMessage('Password reset successful! Redirecting to sign in...');

      // Redirect to success page after 1.5 seconds
      setTimeout(() => {
        navigate('/password-reset-success-page');
      }, 1500);
    } catch (err) {
      console.error('✗ Password reset error:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', met: formData?.password?.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/?.test(formData?.password) },
    { label: 'One lowercase letter', met: /[a-z]/?.test(formData?.password) },
    { label: 'One number', met: /[0-9]/?.test(formData?.password) },
    { label: 'One special character (recommended)', met: /[^a-zA-Z0-9]/?.test(formData?.password) },
  ];

  if (isCheckingToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <>
        <Helmet>
          <title>Invalid Reset Link - Smart Cities Onboarding Platform</title>
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="bg-card border border-border rounded-lg shadow-soft p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-error" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">Invalid Reset Link</h1>
            <p className="text-muted-foreground mb-4">{errors?.token}</p>
            
            {/* Diagnostic Information */}
            {errors?.diagnostic && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-4 text-left">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">{errors?.diagnostic}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Administrator Instructions */}
            <div className="bg-info/10 border border-info/20 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-info mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-foreground mb-2">Administrator Action Required</h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    If password reset emails are redirecting to the Sign In page instead of this Reset Password page:
                  </p>
                  <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to Supabase Dashboard → Authentication → URL Configuration</li>
                    <li>Add <code className="bg-muted px-1 rounded">{window.location?.origin}/password-reset-page</code> to Redirect URLs</li>
                    <li>Go to Authentication → Email Templates → Reset Password</li>
                    <li>Ensure template uses <code className="bg-muted px-1 rounded">{'{{ .ConfirmationURL }}'}</code></li>
                    <li>Check Project Settings → Auth → Site URL is set correctly</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col space-y-2">
              <Button
                variant="default"
                onClick={() => navigate('/user-login')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Login
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location?.reload()}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - Smart Cities Onboarding Platform</title>
        <meta name="description" content="Create a new password for your Smart Cities account" />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-card border border-border rounded-lg shadow-soft p-8 max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
              <Icon name="Lock" size={32} color="white" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">Create New Password</h1>
            <p className="text-muted-foreground">Enter a strong password to secure your account</p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <span className="text-sm text-success font-medium">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Reset Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password Input */}
            <div className="relative">
              <Input
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
                autoComplete="new-password"
                disabled={isLoading || !!successMessage}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-8 h-8 w-8"
                disabled={isLoading || !!successMessage}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
              </Button>
            </div>

            {/* Password Strength Indicator */}
            {formData?.password && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Password Strength:</span>
                  <span className={`font-medium ${passwordStrength?.label === 'Weak' ? 'text-error' : passwordStrength?.label === 'Fair' ? 'text-warning' : passwordStrength?.label === 'Good' ? 'text-info' : 'text-success'}`}>
                    {passwordStrength?.label}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                    style={{ width: `${(passwordStrength?.score / 6) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Password Requirements */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-xs font-medium text-foreground mb-2">Password Requirements:</p>
              {passwordRequirements?.map((req, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon
                    name={req?.met ? 'CheckCircle' : 'Circle'}
                    size={14}
                    className={req?.met ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span className={`text-xs ${req?.met ? 'text-success' : 'text-muted-foreground'}`}>
                    {req?.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Confirm Password Input */}
            <div className="relative">
              <Input
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={formData?.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                error={errors?.confirmPassword}
                required
                autoComplete="new-password"
                disabled={isLoading || !!successMessage}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-8 h-8 w-8"
                disabled={isLoading || !!successMessage}
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
              </Button>
            </div>

            {/* Submit Error */}
            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-error" />
                  <span className="text-sm text-error">{errors?.submit}</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading || !!successMessage}
              iconName="Lock"
              iconPosition="left"
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 pt-6 border-t border-border text-center">
            <Button
              variant="link"
              onClick={() => navigate('/user-login')}
              disabled={isLoading}
              className="text-sm"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PasswordResetPage;